import { useEffect, useMemo, useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2, Route } from 'lucide-react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogClose,
  Form,
  IconButton,
  Notice,
  Skeleton,
  SkeletonGroup,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TextField,
  type BadgeTone,
} from '@/components/play'
import type { SrtBridgeResource, SrtBridgeStatus } from '@/types/admin'

type BridgeForm = {
  name: string
  source_url: string
  destination_url: string
  enabled: boolean
}

const emptyForm: BridgeForm = {
  name: '',
  source_url: '',
  destination_url: '',
  enabled: false,
}

function statusMeta(status: SrtBridgeStatus, enabled: boolean): { label: string; tone: BadgeTone } {
  if (!enabled) {
    return { label: 'Disabled', tone: 'quiet' }
  }
  switch (status.state) {
    case 'connected':
      return { label: 'Connected', tone: 'success' }
    case 'connecting':
      return { label: 'Connecting', tone: 'warning' }
    case 'retrying':
      return { label: 'Retrying', tone: 'warning' }
    case 'lost_lease':
      return { label: 'Lost lease', tone: 'quiet' }
    case 'stopped':
      return { label: 'Stopped', tone: 'quiet' }
    default:
      return { label: status.state ? status.state.charAt(0).toUpperCase() + status.state.slice(1).replace(/_/g, ' ') : 'Configured', tone: 'quiet' }
  }
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

function timeLabel(iso?: string) {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function AdminSrtBridgesPage() {
  const [bridges, setBridges] = useState<SrtBridgeResource[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<BridgeForm>(emptyForm)
  const [error, setError] = useState<string | null>(null)
  // A bridge whose enable or disable request is in flight, so its button can't be pressed twice.
  const [togglingId, setTogglingId] = useState<string | null>(null)
  // The last poll failed. The table keeps the last bridges loaded; with none loaded it isn't shown as empty.
  const [loadFailed, setLoadFailed] = useState(false)

  async function loadBridges() {
    try {
      const res = await api.getAdminSrtBridges()
      setBridges(res.data)
      setLoadFailed(false)
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBridges()
    const interval = setInterval(loadBridges, 5000)
    return () => clearInterval(interval)
  }, [])

  const connectedCount = useMemo(
    () => bridges.filter((bridge) => bridge.attributes.enabled && bridge.attributes.status.state === 'connected').length,
    [bridges]
  )

  function openCreate() {
    setEditingId(null)
    setForm(emptyForm)
    setError(null)
    setShowForm(true)
  }

  async function openEdit(bridge: SrtBridgeResource) {
    setError(null)
    try {
      const res = await api.getAdminSrtBridge(bridge.id)
      const attrs = res.data.attributes
      setEditingId(bridge.id)
      setForm({
        name: attrs.name,
        source_url: attrs.source_url || '',
        destination_url: attrs.destination_url || '',
        enabled: attrs.enabled,
      })
      setShowForm(true)
    } catch {
      setError(`We couldn’t open ${bridge.attributes.name}. Try again.`)
    }
  }

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    try {
      if (editingId) {
        await api.updateAdminSrtBridge(editingId, form)
      } else {
        await api.createAdminSrtBridge(form)
      }
      setShowForm(false)
      await loadBridges()
    } catch (err: any) {
      const errors = err?.response?.data?.error?.errors
      setError(Array.isArray(errors) ? errors.join(', ') : 'Could not save SRT bridge.')
    } finally {
      setSaving(false)
    }
  }

  async function toggleBridge(bridge: SrtBridgeResource) {
    const { enabled, name } = bridge.attributes
    setTogglingId(bridge.id)
    setError(null)
    try {
      if (enabled) {
        await api.disableAdminSrtBridge(bridge.id)
      } else {
        await api.enableAdminSrtBridge(bridge.id)
      }
      await loadBridges()
    } catch {
      setError(`We couldn’t ${enabled ? 'disable' : 'enable'} ${name}. Try again.`)
    } finally {
      setTogglingId(null)
    }
  }

  async function deleteBridge(bridge: SrtBridgeResource) {
    await api.deleteAdminSrtBridge(bridge.id)
    await loadBridges()
  }

  const errorNotice = error && <Notice tone="error" alert>{error}</Notice>

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold">SRT bridges</h1>
            <p className="mt-1 text-sm text-neutral-7">
              {connectedCount}/{bridges.length} connected
            </p>
          </div>
          <Button size="compact" onClick={openCreate}>
            <Plus />
            New bridge
          </Button>
        </div>

        {/* While the form is open the page behind it is inert, so the error shows inside the dialog instead. */}
        {!showForm && errorNotice}
        {loadFailed && (
          <Notice tone="error">
            {bridges.length > 0 ? 'Couldn’t refresh. These are the last bridges loaded.' : 'We couldn’t load SRT bridges.'} Retrying every 5 seconds.
          </Notice>
        )}

        {loading ? (
          <SkeletonGroup label="Loading SRT bridges" className="grid gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
          </SkeletonGroup>
        ) : loadFailed && bridges.length === 0 ? null : (
          <Table caption="SRT bridges" hideCaption>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Bridge</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell>Destination</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell type="number">Sent</TableHeaderCell>
                <TableHeaderCell type="actions"><span className="sr-only">Actions</span></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bridges.map((bridge) => {
                const attrs = bridge.attributes
                const status = statusMeta(attrs.status, attrs.enabled)
                return (
                  <TableRow key={bridge.id}>
                    <TableHeaderCell scope="row">
                      <div>{attrs.name}</div>
                      <div className="font-data text-xs font-normal text-neutral-7">{bridge.id.slice(0, 8)}</div>
                    </TableHeaderCell>
                    <TableCell type="data" className="max-w-[220px] truncate text-xs text-neutral-7">
                      {attrs.source_url_redacted}
                    </TableCell>
                    <TableCell type="data" className="max-w-[220px] truncate text-xs text-neutral-7">
                      {attrs.destination_url_redacted}
                    </TableCell>
                    <TableCell>
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {(attrs.status.next_retry_at || attrs.status.updated_at) && (
                        <div className="mt-1 font-data text-xs text-neutral-7">
                          {attrs.status.next_retry_at ? `Retry at ${timeLabel(attrs.status.next_retry_at)}` : `Updated ${timeLabel(attrs.status.updated_at)}`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell type="number">
                      {formatBytes(attrs.status.bytes_out)}
                    </TableCell>
                    <TableCell type="actions">
                      <div className="flex justify-end gap-1">
                        {/* The icon shows what pressing does; the Status badge says whether the bridge is enabled. */}
                        <IconButton
                          label={`${attrs.enabled ? 'Disable' : 'Enable'} ${attrs.name}`}
                          variant="ghost"
                          size="compact"
                          loading={togglingId === bridge.id}
                          onClick={() => void toggleBridge(bridge)}
                        >
                          {attrs.enabled ? <PowerOff /> : <Power />}
                        </IconButton>
                        <IconButton label={`Edit ${attrs.name}`} variant="ghost" size="compact" onClick={() => void openEdit(bridge)}>
                          <Pencil />
                        </IconButton>
                        <ConfirmDialog
                          trigger={<IconButton label={`Delete ${attrs.name}`} variant="ghost" size="compact"><Trash2 /></IconButton>}
                          title={`Delete bridge "${attrs.name}"?`}
                          description="The relay stops and its settings are removed."
                          confirmLabel="Delete bridge"
                          onConfirm={() => deleteBridge(bridge)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
              {bridges.length === 0 && (
                <TableEmpty colSpan={6} icon={<Route />}>No bridges yet</TableEmpty>
              )}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={showForm}
          onOpenChange={setShowForm}
          size="wide"
          title={editingId ? 'Edit bridge' : 'New bridge'}
          footer={
            <>
              <DialogClose>Cancel</DialogClose>
              <Button type="submit" form="srt-bridge-form" loading={saving}>Save</Button>
            </>
          }
        >
          <Form id="srt-bridge-form" className="space-y-4" onFormSubmit={() => void handleSubmit()}>
            {errorNotice}
            <TextField
              name="name"
              label="Name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
              requiredMessage="Name the bridge."
            />
            <TextField
              name="source_url"
              label="Source SRT URL"
              value={form.source_url}
              onChange={(event) => setForm({ ...form, source_url: event.target.value })}
              required
              requiredMessage="Enter the SRT URL to relay from."
            />
            <TextField
              name="destination_url"
              label="Destination SRT URL"
              value={form.destination_url}
              onChange={(event) => setForm({ ...form, destination_url: event.target.value })}
              required
              requiredMessage="Enter the SRT URL to relay to."
            />
            <Checkbox
              label="Enabled"
              checked={form.enabled}
              onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
            />
          </Form>
        </Dialog>
      </div>
    </AdminLayout>
  )
}
